<script setup>
import StatsCard from "./StatsCard.vue";
import HintSquare from "@/components/global-themed/HintSquare.vue";
import {useQueries} from "@tanstack/vue-query";
import Reporting from '@/services/reporting';
import useQueriesState from "@/composables/useQueriesState";
import useAccountMissing from "@/composables/useAccountMissing";
import useTrafficStore from "@/composables/useTrafficStore";
import SourceGSC from "@/components/reporting/partials/SourceGSC.vue";

const {fromDate, toDate, compareFromDate, compareToDate} = useTrafficStore();

const {isAccountMissing, setIsAccountMissing, checkResponseForAccountMissing} = useAccountMissing();

const fetchVisitorStats = async (fromDate, toDate) => {
  if (!fromDate || !toDate) return {};
  setIsAccountMissing(false);
  try {
    return (await Reporting.fetchVisitorStats(fromDate, toDate)).data.data;
  } catch (e) {
    checkResponseForAccountMissing(e.response);
    throw e;
  }
}

const fetchClicksAndImpressionsStats = async (fromDate, toDate) => {
  if (!fromDate || !toDate) return {};
  setIsAccountMissing(false);
  try {
    return (await Reporting.fetchClicksAndImpressionsStats(fromDate, toDate)).data.data;
  } catch (e) {
    checkResponseForAccountMissing(e.response);
    throw e;
  }
}

const visitorStats = useQueries({
  queries: [
    {
      queryKey: ['reporting-visitor-stats', 'reporting', fromDate, toDate],
      queryFn: async () => fetchVisitorStats(fromDate.value, toDate.value),
    },
    {
      queryKey: ['reporting-visitor-stats', 'reporting', compareFromDate, compareToDate],
      queryFn: async () => fetchVisitorStats(compareFromDate.value, compareToDate.value),
    },
  ],
});

const clicksAndImpressionsStats = useQueries({
  queries: [
    {
      queryKey: ['reporting-clicks-stats', 'reporting', fromDate, toDate],
      queryFn: async () => fetchClicksAndImpressionsStats(fromDate.value, toDate.value),
    },
    {
      queryKey: ['reporting-clicks-stats', 'reporting', compareFromDate, compareToDate],
      queryFn: async () => fetchClicksAndImpressionsStats(compareFromDate.value, compareToDate.value)
    },
  ],
});

const visitorStatsQuery = useQueriesState(visitorStats);
const clicksAndImpressionsStatsQuery = useQueriesState(clicksAndImpressionsStats);

</script>

<template>
  <div class="tw-grid tw-grid-cols-2 tw-gap-8 tw-w-full">
    <div class="tw-grid tw-grid-cols-2 tw-gap-8 tw-w-full tw-h-auto tw-flex-grow-0 tw-mb-auto">
      <StatsCard
        :value="visitorStatsQuery.data.value?.visitors"
        :compare="visitorStatsQuery.comparedData.value?.visitors"
        :loading="visitorStatsQuery.isLoading.value"
        :account-missing="isAccountMissing"
        :error="visitorStatsQuery.isError.value"
      >
        <template #title>
          <span>Visitors</span>
          <HintSquare
            :hint="{html: `The total number of visits to the website<br/>during the latest time period selected.`}"
          />
        </template>
      </StatsCard>

      <StatsCard
        :value="visitorStatsQuery.data.value?.engagementRate"
        :compare="visitorStatsQuery.comparedData.value?.engagementRate"
        :loading="visitorStatsQuery.isLoading.value"
        :account-missing="isAccountMissing"
        :error="visitorStatsQuery.isError.value"
        :formatter="(value) => `${value?.toFixed(2)}%`"
      >
        <template #title>
          <span>Engagement Rate</span>
          <HintSquare
            :hint="{html: `The Engagement rate is the percentage of visits in which a person<br/>stays on your website for more than 10 seconds, views more<br/>than one page, or fires a conversion event.`}"
          />
        </template>
      </StatsCard>
    </div>

    <div class="tw-grid tw-grid-cols-2 tw-gap-8 tw-w-full tw-h-auto tw-flex-grow-0 tw-mb-auto">
      <StatsCard
        :value="clicksAndImpressionsStatsQuery.data.value?.clicks"
        :compare="clicksAndImpressionsStatsQuery.comparedData.value?.clicks"
        :loading="clicksAndImpressionsStatsQuery.isLoading.value"
        :error="clicksAndImpressionsStatsQuery.isError.value"
      >
        <template #title>
          <span>Clicks</span>
          <HintSquare
            :hint="{html: `The total number of clicks to the website from Google<br/>search during the latest time period selected.`}"
          />
        </template>
        <template #source>
          <SourceGSC />
        </template>
      </StatsCard>

      <StatsCard
        :value="clicksAndImpressionsStatsQuery.data.value?.impressions"
        :compare="clicksAndImpressionsStatsQuery.comparedData.value?.impressions"
        :loading="clicksAndImpressionsStatsQuery.isLoading.value"
        :error="clicksAndImpressionsStatsQuery.isError.value"
      >
        <template #title>
          <span>Impressions</span>
          <HintSquare
            :hint="{html: `The total number of impressions the website had in<br/>Google search during the latest time period selected.`}"
          />
        </template>
        <template #source>
          <SourceGSC />
        </template>
      </StatsCard>
    </div>
  </div>
</template>